import { combineSx, styled, useTheme } from '@chronosphereio/chrono-ui';
import { GroupedEventsIcon } from '@chronosphereio/shared';
import { cloneElement, ReactNode, useMemo } from 'react';
import { EventItem } from '@/core';

const TIMELINE_EVENT_ICON_SIZE = 16;

export interface EventIconProps {
  event: EventItem;
  x: number;
  y: number;
  onClick?: (event: EventItem) => void;
}

/**
 * Renders an event icon at a specific position
 */
export function EventIcon({ event, x, y, onClick }: EventIconProps) {
  const { theme } = useTheme();
  const eventColor =
    event.color ??
    (theme.featureFlags.panwRebrandStubYear ? theme.palette.status.default.main : theme.palette.grey[400]);
  const icon = event.icon ?? 'circle';

  const iconElement = useMemo(() => {
    const whiteIconColor = theme.palette.common.white;

    // If icon is a React element (from category.icon), wrap with diamond background
    // Only EventPayload has category, EventGroupPayload does not
    if ('category' in event && event.category && 'icon' in event.category && event.category.icon) {
      const clonedIcon = cloneElement(event.category.icon, {
        sx: combineSx(event.category.icon.props.sx, {
          width: TIMELINE_EVENT_ICON_SIZE * 0.75,
          height: TIMELINE_EVENT_ICON_SIZE * 0.75,
          color: whiteIconColor,
        }),
      });
      return <IconBackground color={eventColor}>{clonedIcon}</IconBackground>;
    }

    // For EventGroupPayload, use GroupedEventsIcon with diamond background
    if ('totalCount' in event && event.totalCount > 1) {
      return (
        <IconBackground color={eventColor} sizeMultiplier={0.95}>
          <GroupedEventsIcon
            width={TIMELINE_EVENT_ICON_SIZE * 0.5}
            height={TIMELINE_EVENT_ICON_SIZE * 0.5}
            fill={whiteIconColor}
          />
        </IconBackground>
      );
    }

    // Render simple shape icons - wrap all with background diamond
    const iconSize = TIMELINE_EVENT_ICON_SIZE * 0.75; // Slightly smaller icon inside diamond

    switch (icon) {
      case 'circle':
        return (
          <IconBackground color={eventColor}>
            <IconCircle size={iconSize} />
          </IconBackground>
        );
      case 'rect':
        return (
          <IconBackground color={eventColor}>
            <IconRect size={iconSize} />
          </IconBackground>
        );
      case 'roundRect':
        return (
          <IconBackground color={eventColor}>
            <IconRoundRect size={iconSize} />
          </IconBackground>
        );
      case 'triangle':
        return (
          <IconBackground color={eventColor}>
            <IconTriangle size={iconSize} />
          </IconBackground>
        );
      case 'diamond':
        return (
          <IconBackground color={eventColor}>
            <IconDiamond size={iconSize} />
          </IconBackground>
        );
      default:
        return (
          <IconBackground color={eventColor}>
            <IconCircle size={iconSize} />
          </IconBackground>
        );
    }
  }, [event, eventColor, icon, theme]);

  return (
    <IconWrapper x={x} y={y} title={event.title} onClick={() => onClick?.(event)}>
      {iconElement}
    </IconWrapper>
  );
}

/**
 * Wraps an icon with a diamond-shaped background
 */
function IconBackground({
  children,
  color,
  sizeMultiplier = 1.05,
}: {
  children: ReactNode;
  color: string;
  sizeMultiplier?: number;
}) {
  return (
    <DiamondBackground color={color} sizeMultiplier={sizeMultiplier}>
      <IconContainer>{children}</IconContainer>
    </DiamondBackground>
  );
}

/**
 * Diamond-shaped background container for event icons
 */
const DiamondBackground = styled('div')<{ color: string; sizeMultiplier?: number }>(
  ({ color, sizeMultiplier = 1.05 }) => {
    const diamondSize = TIMELINE_EVENT_ICON_SIZE * sizeMultiplier;
    return {
      width: diamondSize,
      height: diamondSize,
      backgroundColor: color,
      transform: 'rotate(45deg)',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  }
);

/**
 * Inner container that counter-rotates the icon content
 */
const IconContainer = styled('div')({
  transform: 'rotate(-45deg)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Positioned container for the icon with hover effects
 */
const IconWrapper = styled('div')<{ x: number; y: number }>(({ x, y }) => ({
  position: 'absolute',
  left: `${x}px`,
  top: `${y}px`,
  transform: 'translate(-50%, -50%)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translate(-50%, -50%) scale(1.2)',
    transition: 'transform 0.1s ease-out',
  },
}));

const IconCircle = styled('div')<{ size: number }>(({ theme, size }) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  backgroundColor: theme.palette.common.white,
}));

const IconRect = styled('div')<{ size: number }>(({ theme, size }) => ({
  width: size,
  height: size,
  backgroundColor: theme.palette.common.white,
}));

const IconRoundRect = styled('div')<{ size: number }>(({ theme, size }) => ({
  width: size,
  height: size,
  borderRadius: '4px',
  backgroundColor: theme.palette.common.white,
}));

const IconTriangle = styled('div')<{ size: number }>(({ theme, size }) => ({
  width: 0,
  height: 0,
  borderLeft: `${size / 2}px solid transparent`,
  borderRight: `${size / 2}px solid transparent`,
  borderBottom: `${size}px solid ${theme.palette.common.white}`,
}));

const IconDiamond = styled('div')<{ size: number }>(({ theme, size }) => ({
  width: size,
  height: size,
  backgroundColor: theme.palette.common.white,
  transform: 'rotate(45deg)',
}));
